import { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { logError } from '../lib/logger';
import styles from './PerformanceAnalytics.module.css';
import {
  binPnls,
  equityToPolylinePoints,
  formatDuration,
  formatProfitFactor,
  formatSignedUsd,
  TIMEFRAME_LABELS,
  type PerformancePayload,
  type TimeframeLabel,
  type TimeframeStats,
} from './performance.logic';

const CURVE_W = 600;
const CURVE_H = 220;
const HIST_W = 600;
const HIST_H = 160;
const HIST_BINS = 12;
const PAD = 6;

interface Props {
  serverUrl: string;
}

/** Rounded-top column: 4px radius at the data end, square at the baseline. */
function columnPath(x: number, top: number, width: number, baseline: number): string {
  const r = Math.min(4, width / 2, Math.max(baseline - top, 0));
  return [
    `M ${x} ${baseline}`,
    `L ${x} ${top + r}`,
    `Q ${x} ${top} ${x + r} ${top}`,
    `L ${x + width - r} ${top}`,
    `Q ${x + width} ${top} ${x + width} ${top + r}`,
    `L ${x + width} ${baseline}`,
    'Z',
  ].join(' ');
}

export function PerformanceAnalytics({ serverUrl }: Props): JSX.Element {
  const { t } = useLanguage();
  const [payload, setPayload] = useState<PerformancePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [timeframe, setTimeframe] = useState<TimeframeLabel>('1M');
  const [hoverPoint, setHoverPoint] = useState<number | null>(null);
  const [hoverBin, setHoverBin] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setFailed(false);
      try {
        const res = await fetch(`${serverUrl}/api/analytics/performance`);
        if (!res.ok) throw new Error(`Analytics endpoint returned HTTP ${res.status}`);
        const data: PerformancePayload = await res.json();
        if (!cancelled) setPayload(data);
      } catch (err) {
        if (!cancelled) setFailed(true);
        void logError(err, 'PerformanceAnalytics.fetch');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [serverUrl]);

  const [showTable, setShowTable] = useState(false);

  // Tolerate a malformed/unexpected payload shape — never crash the tab.
  const stats: TimeframeStats | null = payload?.timeframes?.[timeframe] ?? null;
  const bins = useMemo(() => {
    if (stats?.pnl_bins && stats.pnl_bins.length > 0) {
      return stats.pnl_bins;
    }
    return binPnls(stats?.trade_pnls ?? [], HIST_BINS);
  }, [stats]);
  const curvePoints = useMemo(
    () => equityToPolylinePoints(stats?.equity_curve ?? [], CURVE_W, CURVE_H, PAD),
    [stats],
  );
  const curveXY = useMemo(
    () => curvePoints.split(' ').filter(Boolean).map((p) => p.split(',').map(Number)),
    [curvePoints],
  );

  const maxBinCount = bins.reduce((acc, b) => Math.max(acc, b.count), 0);
  const histPadLeft = 40;
  const histPadBottom = 30;
  const histPadTop = 15;
  const histPadRight = 15;
  const plotW = HIST_W - histPadLeft - histPadRight;
  const plotH = HIST_H - histPadTop - histPadBottom;
  const histBaseline = HIST_H - histPadBottom;
  const binSlot = plotW / Math.max(bins.length, 1);
  const barWidth = Math.min(24, Math.max(binSlot - 2, 2));

  const isPositive = (stats?.net_pnl_usd ?? 0) >= 0;
  const chartStyle = {
    '--gx-chart-color': isPositive ? 'var(--gx-success)' : 'var(--gx-danger)',
    '--gx-chart-gradient': isPositive ? 'url(#equity-gradient-pos)' : 'url(#equity-gradient-neg)',
  } as React.CSSProperties;


  return (
    <div className={styles.panel}>
      <h2 className={styles.panelTitle}>{t('analytics.title')}</h2>

      <div className={styles.chipRow} role="group" aria-label={t('analytics.timeframe')}>
        {TIMEFRAME_LABELS.map((label) => {
          const count = payload?.timeframes?.[label]?.trade_count;
          const countText = count !== undefined ? ` · ${count}` : '';
          const isActive = timeframe === label;
          const isDisabled = count === 0;

          return (
            <button
              key={label}
              type="button"
              className={
                isActive
                  ? styles.chipActive
                  : isDisabled
                  ? styles.chipDisabled
                  : styles.chip
              }
              disabled={isDisabled}
              aria-pressed={isActive}
              onClick={() => setTimeframe(label)}
            >
              {label}{countText}
            </button>
          );
        })}
      </div>

      {loading && <div className={styles.stateMsg}>{t('analytics.loading')}</div>}
      {failed && !loading && <div className={styles.stateMsgError}>{t('analytics.error')}</div>}

      {!loading && !failed && stats && (
        <>
          <div className={styles.tileRow}>
            <div className={styles.tile}>
              <span className={styles.tileLabel}>{t('analytics.netPnl')}</span>
              <span
                className={
                  stats.net_pnl_usd >= 0 ? styles.tileValuePositive : styles.tileValueNegative
                }
              >
                {formatSignedUsd(stats.net_pnl_usd)}
              </span>
            </div>
            <div className={styles.tile}>
              <span className={styles.tileLabel}>{t('analytics.winRate')}</span>
              <span className={styles.tileValue}>{stats.win_rate_pct.toFixed(1)}%</span>
              <span className={styles.tileSubLabel}>
                {stats.wins} {t('analytics.outOf')} {stats.trade_count}
              </span>
            </div>
            <div className={styles.tile}>
              <span className={styles.tileLabel}>{t('analytics.profitFactor')}</span>
              <span className={styles.tileValue}>{formatProfitFactor(stats.profit_factor)}</span>
            </div>
            <div className={styles.tile}>
              <span className={styles.tileLabel}>{t('analytics.sharpe')}</span>
              <span className={styles.tileValue}>{stats.sharpe_ratio.toFixed(2)}</span>
            </div>
            <div className={styles.tile}>
              <span className={styles.tileLabel}>{t('analytics.maxDrawdown')}</span>
              <span className={styles.tileValue}>{stats.max_drawdown_pct.toFixed(2)}%</span>
            </div>
            <div className={styles.tile}>
              <span className={styles.tileLabel}>{t('analytics.avgDuration')}</span>
              <span className={styles.tileValue}>{formatDuration(stats.avg_duration_seconds)}</span>
            </div>
          </div>

          {stats.trade_count === 0 ? (
            <div className={styles.stateMsg}>{t('analytics.empty')}</div>
          ) : (
            <>
              <div className={styles.chartBlock}>
                <h3 className={styles.chartTitle}>{t('analytics.equityCurve')}</h3>
                <div className={styles.chartWrap}>
                  <svg
                    className={styles.chartSvg}
                    style={chartStyle}
                    viewBox={`0 0 ${CURVE_W} ${CURVE_H}`}
                    role="img"
                    aria-label={t('analytics.equityCurve')}
                    onMouseLeave={() => setHoverPoint(null)}
                  >
                    <defs>
                      <linearGradient id="equity-gradient-pos" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--gx-success)" stopOpacity="0.25" />
                        <stop offset="100%" stopColor="var(--gx-success)" stopOpacity="0" />
                      </linearGradient>
                      <linearGradient id="equity-gradient-neg" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--gx-danger)" stopOpacity="0.25" />
                        <stop offset="100%" stopColor="var(--gx-danger)" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    {[0.25, 0.5, 0.75].map((f) => (
                      <line
                        key={f}
                        className={styles.gridline}
                        x1={PAD}
                        x2={CURVE_W - PAD}
                        y1={CURVE_H * f}
                        y2={CURVE_H * f}
                      />
                    ))}
                    {curveXY.length > 1 && (
                      <polygon
                        className={styles.equityArea}
                        points={`${PAD},${CURVE_H - PAD} ${curvePoints} ${CURVE_W - PAD},${CURVE_H - PAD}`}
                        fill="var(--gx-chart-gradient)"
                      />
                    )}
                    <polyline className={styles.equityLineBg} points={curvePoints} />
                    <polyline className={styles.equityLine} points={curvePoints} />
                    {curveXY.length > 0 && (
                      <>
                        <circle
                          className={styles.equityEndDot}
                          cx={curveXY[curveXY.length - 1][0]}
                          cy={curveXY[curveXY.length - 1][1]}
                          r={4.5}
                        />
                        <text
                          className={styles.endLabel}
                          x={curveXY[curveXY.length - 1][0] - 10}
                          y={Math.max(curveXY[curveXY.length - 1][1] - 10, 14)}
                          textAnchor="end"
                        >
                          {formatSignedUsd(
                            stats.equity_curve[stats.equity_curve.length - 1].equity,
                          ).replace('+', '')}
                        </text>
                      </>
                    )}
                    {hoverPoint !== null && curveXY[hoverPoint] && (
                      <line
                        className={styles.crosshair}
                        x1={curveXY[hoverPoint][0]}
                        x2={curveXY[hoverPoint][0]}
                        y1={PAD}
                        y2={CURVE_H - PAD}
                      />
                    )}
                    {curveXY.map(([x], i) => (
                      <rect
                        key={i}
                        className={styles.hitArea}
                        x={x - CURVE_W / Math.max(curveXY.length, 1) / 2}
                        y={0}
                        width={CURVE_W / Math.max(curveXY.length, 1)}
                        height={CURVE_H}
                        data-testid={`equity-hit-${i}`}
                        onMouseEnter={() => setHoverPoint(i)}
                      />
                    ))}
                  </svg>
                  {hoverPoint !== null && stats.equity_curve[hoverPoint] && (
                    <div className={styles.tooltip} role="status">
                      {new Date(stats.equity_curve[hoverPoint].t * 1000).toLocaleString()} ·{' '}
                      {formatSignedUsd(stats.equity_curve[hoverPoint].equity).replace('+', '')}
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.chartsRow}>
                <div className={styles.chartBlock}>
                  <h3 className={styles.chartTitle}>{t('analytics.pnlHistogram')}</h3>
                  <div className={styles.chartWrap}>
                    <svg
                      className={styles.chartSvg}
                      viewBox={`0 0 ${HIST_W} ${HIST_H}`}
                      role="img"
                      aria-label={t('analytics.pnlHistogram')}
                      onMouseLeave={() => setHoverBin(null)}
                    >
                      {maxBinCount > 0 && [0.25, 0.5, 0.75, 1.0].map((f) => {
                        const y = histBaseline - plotH * f;
                        const countVal = Math.round(maxBinCount * f);
                        return (
                          <g key={f}>
                            <line
                              className={styles.gridline}
                              x1={histPadLeft}
                              x2={HIST_W - histPadRight}
                              y1={y}
                              y2={y}
                            />
                            <text
                              className={styles.axisLabel}
                              x={histPadLeft - 8}
                              y={y + 4}
                              textAnchor="end"
                            >
                              {countVal}
                            </text>
                          </g>
                        );
                      })}
                      <line
                        className={styles.gridline}
                        x1={histPadLeft}
                        x2={HIST_W - histPadRight}
                        y1={histBaseline}
                        y2={histBaseline}
                      />
                      {bins.map((bin, i) => {
                        if (bin.count === 0) return null;
                        const x = histPadLeft + i * binSlot + (binSlot - barWidth) / 2;
                        const barH = (bin.count / Math.max(maxBinCount, 1)) * plotH;
                        return (
                          <path
                            key={i}
                            className={bin.isGain ? styles.barGain : styles.barLoss}
                            d={columnPath(x, histBaseline - barH, barWidth, histBaseline)}
                            data-testid={`pnl-bar-${i}`}
                            onMouseEnter={() => setHoverBin(i)}
                          />
                        );
                      })}
                      {bins.length > 0 && (
                        <>
                          <text
                            className={styles.axisLabel}
                            x={histPadLeft}
                            y={HIST_H - 12}
                            textAnchor="start"
                          >
                            {formatSignedUsd(bins[0].from)}
                          </text>
                          <text
                            className={styles.axisLabel}
                            x={histPadLeft + plotW / 2}
                            y={HIST_H - 12}
                            textAnchor="middle"
                          >
                            {formatSignedUsd((bins[0].from + bins[bins.length - 1].to) / 2)}
                          </text>
                          <text
                            className={styles.axisLabel}
                            x={histPadLeft + plotW}
                            y={HIST_H - 12}
                            textAnchor="end"
                          >
                            {formatSignedUsd(bins[bins.length - 1].to)}
                          </text>
                        </>
                      )}
                      <text
                        className={styles.axisTitle}
                        transform={`rotate(-90) translate(${-histBaseline + plotH / 2}, 12)`}
                        textAnchor="middle"
                      >
                        {t('analytics.tradeCount')}
                      </text>
                    </svg>
                    {hoverBin !== null && bins[hoverBin] && (
                      <div className={styles.tooltip} role="status">
                        {formatSignedUsd(bins[hoverBin].from)} … {formatSignedUsd(bins[hoverBin].to)}{' '}
                        · {bins[hoverBin].count} {t('analytics.trades')}
                      </div>
                    )}
                  </div>
                </div>

                <div className={styles.chartBlock}>
                  <h3 className={styles.chartTitle}>{t('analytics.winLoss')}</h3>
                  <div className={styles.meterLabels}>
                    <span className={styles.meterWin}>
                      {t('analytics.wins')}: {stats.wins}
                    </span>
                    <span className={styles.meterLoss}>
                      {t('analytics.losses')}: {stats.losses}
                    </span>
                  </div>
                  <div
                    className={styles.meterTrack}
                    role="img"
                    aria-label={`${t('analytics.wins')} ${stats.wins} / ${t('analytics.losses')} ${stats.losses}`}
                  >
                    <div
                      className={styles.meterFillWin}
                      style={{ flexGrow: Math.max(stats.wins, stats.wins + stats.losses === 0 ? 1 : 0) }}
                    />
                    <div className={styles.meterFillLoss} style={{ flexGrow: stats.losses }} />
                  </div>
                  <div className={styles.meterCaption}>
                    {stats.trade_count} {t('analytics.trades')}
                  </div>
                </div>
              </div>

              <div className={styles.tableSection}>
                <button
                  type="button"
                  className={styles.tableToggleBtn}
                  onClick={() => setShowTable(!showTable)}
                  aria-expanded={showTable}
                >
                  <span className={`${styles.chevron} ${showTable ? styles.chevronOpen : ''}`}>
                    ▶
                  </span>
                  <span className={styles.toggleText}>{t('analytics.dataTable')}</span>
                  <span className={styles.rowBadge}>{stats.equity_curve.length}</span>
                </button>

                {showTable && (
                  <div className={styles.tableWrap}>
                    <table className={styles.dataTable}>
                      <thead>
                        <tr>
                          <th>{t('analytics.tableTime')}</th>
                          <th>{t('analytics.tableEquity')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.equity_curve.map((point, i) => (
                          <tr key={i}>
                            <td>{new Date(point.t * 1000).toLocaleString()}</td>
                            <td>{formatSignedUsd(point.equity).replace('+', '')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
